"use client";
import InpSafeForm from "@/components/atoms/FormComponents/InpSafeForm";

import { Controller } from "react-hook-form";
import Input from "@/components/atoms/FormComponents/Input/Input";
import styles from "./BlogConnectModalForm.module.scss";
import cn from "clsx";
import SubmitButton from "@/components/atoms/FormComponents/SubmitButton";
import Checkbox from "@/components/atoms/FormComponents/Checkbox";
import SelectDark from "@/revamp/atoms/FormComponents/Select/SelectDark";
import ApplicationFormRedirectLoader from "@/components/molecules/ApplicationFormRedirectLoader/ApplicationFormRedirectLoader";
import ConsentFormPara from "@/components/common/utils/consentFormPara";
import CountryDropdownGlobal from "@/components/atoms/FormComponents/CustomDropdownGlobal/CustomDropdownGlobal";
import { useBlogConnectModalFormController } from "./controller/useBlogConnectModalFormController";
import type { BlogConnectModalFormProps } from "./model/blogConnectModalForm.types";

const BlogConnectModalForm = (props: BlogConnectModalFormProps) => {
  const {
    control,
    errors,
    isSubmitting,
    isOTPLoading,
    programsList,
    showProgramField,
    isApplicationLoader,
    loadingCount,
    countries,
    handleFieldChange,
    handleDegreeChange,
    handleProgramChange,
    handlePhoneNumberChange,
    handleFieldBlur,
    updateCountryCode,
    handleFormSubmit,
    degreeOptions,
    SELECT_DEGREE,
    SELECT_PROGRAM,
  } = useBlogConnectModalFormController(props);

  const fieldsDisabled = isSubmitting || isOTPLoading;

  return (
    <>
      {isApplicationLoader ? (
        <ApplicationFormRedirectLoader
          redirectTime={loadingCount}
          isDarkForm={true}
        />
      ) : (
        <InpSafeForm onSubmit={handleFormSubmit} className={cn(styles.root)}>
          <div className={cn(styles.inputWrapper)}>
            <label htmlFor="full_name">full name *</label>
            <Controller
              name="fullName"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    isDark={true}
                    placeholder="Enter your full name"
                    value={field.value ?? ""}
                    onChangeFunc={(value: string) =>
                      handleFieldChange("fullName", value)
                    }
                    onBlur={() => {
                      field.onBlur();
                      handleFieldBlur(
                        "fullName",
                        "",
                        fieldState.error?.message || errors.fullName || "",
                      );
                    }}
                    disabled={fieldsDisabled}
                    id="full_name"
                  />
                  <p className={cn(styles.error)}>{errors.fullName}</p>
                </>
              )}
            />
          </div>
          <div className={cn(styles.inputWrapper)}>
            <label htmlFor="enter_phone_no">phone number *</label>
            <div className="flex gap-[6px]">
              <div>
                <Controller
                  name="countryCode"
                  control={control}
                  render={({ field }) => (
                    <CountryDropdownGlobal
                      onChangeFunc={(value: string) =>
                        handleFieldChange("countryCode", value)
                      }
                      countries={countries}
                      countryCode={field.value}
                      updateCountryCode={updateCountryCode}
                      disabled={fieldsDisabled}
                      isDark={true}
                    />
                  )}
                />
              </div>
              <div className="w-full">
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Input
                      isDark={true}
                      placeholder="Enter your no."
                      value={field.value ?? ""}
                      onChangeFunc={handlePhoneNumberChange}
                      onBlur={() => {
                        field.onBlur();
                        handleFieldBlur(
                          "phoneNumber",
                          "",
                          fieldState.error?.message || errors.phoneNumber || "",
                        );
                      }}
                      disabled={fieldsDisabled}
                      id="enter_phone_no"
                      isTel
                    />
                  )}
                />
              </div>
            </div>
            <p className={cn(styles.error)}>
              {errors.countryCode || errors.phoneNumber}
            </p>
          </div>
          <div className={cn(styles.inputWrapper)}>
            <label htmlFor="email_id">email id *</label>
            <Controller
              name="emailId"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    isDark={true}
                    placeholder="abc@xyz.com"
                    value={field.value ?? ""}
                    onChangeFunc={(value: string) =>
                      handleFieldChange("emailId", value)
                    }
                    onBlur={() => {
                      field.onBlur();
                      handleFieldBlur(
                        "emailId",
                        "",
                        fieldState.error?.message || errors.emailId || "",
                      );
                    }}
                    disabled={fieldsDisabled}
                    id="email_id"
                  />
                  <p className={cn(styles.error)}>{errors.emailId}</p>
                </>
              )}
            />
          </div>
          {showProgramField && (
            <div className={cn(styles.inputWrapper)}>
              <label htmlFor="degree_lookup">
                Degree you are looking for *
              </label>
              <Controller
                name="degree"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <SelectDark
                      value={field.value ?? ""}
                      onChangeFunc={handleDegreeChange}
                      options={[SELECT_DEGREE, ...degreeOptions]}
                      disabled={fieldsDisabled}
                      id="degree_lookup"
                    />
                    <p className={cn(styles.error)}>{errors.degree}</p>
                  </>
                )}
              />
            </div>
          )}
          {showProgramField && (
            <div className={cn(styles.commonMargins, "w-full")}>
              <label htmlFor="degree_lookup">
                Programs you are looking for *
              </label>
              <Controller
                name="program"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <SelectDark
                      value={field.value ?? ""}
                      onChangeFunc={handleProgramChange}
                      options={[SELECT_PROGRAM, ...programsList]}
                      disabled={fieldsDisabled}
                      id="degree_lookup"
                    />
                    <p className={cn(styles.error)}>{errors.program}</p>
                  </>
                )}
              />
            </div>
          )}
          <div
            className={cn(styles.commonMargins, "flex items-start gap-[17px]")}
          >
            <div className="shrink-0">
              <Controller
                name="isTermsAccepted"
                control={control}
                render={({ field, fieldState }) => (
                  <Checkbox
                    isDark={true}
                    name="consent"
                    value={field.value}
                    onChangeFunc={(value: boolean) =>
                      handleFieldChange("isTermsAccepted", value)
                    }
                    onBlur={() => {
                      field.onBlur();
                      handleFieldBlur(
                        "isTermsAccepted",
                        "",
                        fieldState.error?.message || errors.isTermsAccepted || "",
                      );
                    }}
                    disabled={fieldsDisabled}
                    id="terms-conditions"
                  />
                )}
              />
            </div>
            <p className={cn(styles.authorization)}>
              <ConsentFormPara />
            </p>
          </div>
          <SubmitButton
            id="blogconnect_submit"
            bgColor="#ffc907"
            textColor="#1a325d"
            text={fieldsDisabled ? "Submitting" : "Submit"}
            isDisabled={fieldsDisabled}
          />
        </InpSafeForm>
      )}
    </>
  );
};

export default BlogConnectModalForm;
